import { motion } from 'framer-motion';
import { CloudRain, RefreshCw, Globe2, AlertTriangle, Check } from 'lucide-react';
import { CloudText, Reveal } from '@/components/CloudText';
import { WEBSITE_REQUIREMENTS, WEBSITE_REVISIONS, DOMAIN_UPKEEP, PROJECT_POLICY, CLOSING } from '@/data/content';
import { scrollToSection } from '@/lib/scroll';

function RainPanel({ icon: Icon, eyebrow, title, children, className = '', testId }) {
  return (
    <motion.article whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 180, damping: 20 }} className={`glass-rain relative overflow-hidden rounded-[2rem] p-7 sm:p-9 ${className}`} data-testid={testId} data-cursor="cloud">
      <p className="flex items-center gap-2 font-display text-[0.6rem] tracking-[0.4em] uppercase rain-eyebrow"><Icon size={13} /> {eyebrow}</p>
      <h3 className="cloud-text cloud-text-rain mt-4 text-2xl sm:text-3xl uppercase tracking-tight">{title}</h3>
      <div className="relative mt-5">{children}</div>
    </motion.article>
  );
}

export function WebsiteFinePrint() {
  return (
    <div className="mt-28" data-testid="website-fine-print">
      <p className="cloud-eyebrow rain-eyebrow mb-5">Before development begins — the fine print, in plain sight</p>
      <CloudText lines={['WEBSITE PROJECT', 'REQUIREMENTS']} size="text-3xl sm:text-5xl lg:text-6xl" className="cloud-text-rain" testId="requirements-title" />
      <Reveal delay={0.2}><p className="mt-6 text-base text-white/75 max-w-2xl">All website clients must provide the necessary creative materials and project information before development begins.</p></Reveal>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="requirements-grid">
        {WEBSITE_REQUIREMENTS.map((r, i) => (
          <Reveal key={r.title} delay={i * 0.08}>
            <motion.article whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 180, damping: 20 }} className="glass-rain relative h-full rounded-[1.75rem] p-6 sm:p-7" data-testid={`requirement-${i + 1}`} data-cursor="cloud">
              <span className="font-display text-4xl font-extrabold cloud-text-rain">0{i + 1}</span>
              <h4 className="font-display text-base sm:text-lg font-bold uppercase tracking-tight mt-3 text-white">{r.title}</h4>
              <p className="mt-3 text-sm text-white/85 leading-relaxed">{r.body}</p>
            </motion.article>
          </Reveal>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
        <Reveal className="lg:col-span-5" delay={0.1}>
          <RainPanel icon={RefreshCw} eyebrow="Website revisions" title="Changes after approval" className="h-full" testId="revisions-panel">
            <p className="text-sm text-white/85 leading-relaxed">{WEBSITE_REVISIONS.intro}</p>
            <p className="mt-6 flex items-baseline gap-3"><span className="font-display text-5xl sm:text-6xl font-extrabold text-white" style={{ textShadow: '0 0 30px rgba(249,115,22,0.45)' }} data-testid="revision-price">{WEBSITE_REVISIONS.price}</span><span className="font-display text-[0.65rem] tracking-[0.35em] uppercase text-[#ffb37a]">{WEBSITE_REVISIONS.unit}</span></p>
            <p className="mt-5 text-sm text-white/85 leading-relaxed"><span className="font-semibold text-white">What counts as a revision: </span>{WEBSITE_REVISIONS.definition}</p>
            <p className="mt-3 text-sm text-white/70 leading-relaxed">{WEBSITE_REVISIONS.note}</p>
          </RainPanel>
        </Reveal>
        <Reveal className="lg:col-span-7" delay={0.2}>
          <RainPanel icon={Globe2} eyebrow="Domain & website upkeep" title="Billed separately" className="h-full" testId="upkeep-panel">
            <p className="text-sm text-white/85 leading-relaxed">{DOMAIN_UPKEEP.intro}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {DOMAIN_UPKEEP.items.map((it) => (
                <div key={it.title} className="rounded-2xl bg-[#061a3d]/35 ring-1 ring-white/15 p-5">
                  <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{it.title}</p>
                  <p className="mt-2 text-sm text-white/80 leading-relaxed">{it.body}</p>
                </div>
              ))}
            </div>
            <p className="mt-5 text-sm text-white/70 leading-relaxed">{DOMAIN_UPKEEP.note}</p>
          </RainPanel>
        </Reveal>
      </div>

      <Reveal className="mt-6" delay={0.1}>
        <RainPanel icon={AlertTriangle} eyebrow="Important project policy" title="Starting prices are starting points" testId="policy-panel">
          <p className="text-sm text-white/85 leading-relaxed max-w-3xl">{PROJECT_POLICY.intro}</p>
          <ul className="mt-5 flex flex-wrap gap-2.5" data-testid="policy-factors">
            {PROJECT_POLICY.factors.map((f) => (
              <li key={f} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm text-white/90 ring-1 ring-white/20"><Check size={13} className="text-[#ffb37a]" /> {f}</li>
            ))}
          </ul>
          <p className="mt-6 font-display text-sm sm:text-base font-semibold text-white">{PROJECT_POLICY.close}</p>
        </RainPanel>
      </Reveal>

      <div className="relative mt-28 text-center" data-testid="pricing-closing">
        <div className="absolute inset-x-[10%] top-0 h-full rounded-[50%] pointer-events-none" style={{ background: 'radial-gradient(ellipse, rgba(255,255,255,0.14), rgba(255,255,255,0) 65%)', filter: 'blur(40px)' }} />
        <p className="cloud-eyebrow mb-6 flex items-center justify-center gap-2"><CloudRain size={14} /> StorySpark Studios</p>
        <div className="flex justify-center"><CloudText lines={CLOSING.lines} size="text-3xl sm:text-5xl lg:text-6xl" testId="closing-title" /></div>
        <p className="mt-8 font-display text-[0.65rem] sm:text-xs tracking-[0.35em] uppercase text-white/75">
          {CLOSING.tags.map((t, i) => <span key={t}>{i > 0 && <span className="text-[#f97316] mx-3">•</span>}{t}</span>)}
        </p>
        <button type="button" onClick={() => scrollToSection('start')} className="btn-cloud primary mt-10" data-testid="closing-cta">Start your project</button>
      </div>
    </div>
  );
}
